import { Controller } from "@hotwired/stimulus"

// Flash 메시지 관리 컨트롤러
export default class extends Controller {
  static targets = ["message", "closeButton"]
  static values = { 
    autoHide: { type: Boolean, default: true },
    hideDelay: { type: Number, default: 5000 }
  }

  connect() {
    console.log("Flash message controller connected")
    
    // 중복 메시지 제거
    this.removeDuplicateMessages()
    
    // 자동 숨김 설정
    if (this.autoHideValue) {
      this.setupAutoHide()
    }
    
    // 애니메이션 효과 추가
    this.addEntranceAnimation()
  }

  disconnect() {
    // 타이머 정리
    if (this.autoHideTimer) {
      clearTimeout(this.autoHideTimer)
      this.autoHideTimer = null
    }
  }

  // 중복 메시지 제거
  removeDuplicateMessages() {
    const allMessages = document.querySelectorAll('[data-flash-message="true"]')
    const messageTexts = new Set()
    
    allMessages.forEach(message => {
      const messageText = message.querySelector('span')?.textContent?.trim()
      const messageType = message.dataset.flashType
      const messageKey = `${messageType}:${messageText}`
      
      if (messageTexts.has(messageKey)) {
        // 중복 메시지 제거
        console.log("Removing duplicate flash message:", messageText)
        message.remove()
      } else {
        messageTexts.add(messageKey)
      }
    })
  }

  // 자동 숨김 설정
  setupAutoHide() {
    this.messageTargets.forEach(message => {
      if (!message.dataset.autoHideInitialized) {
        message.dataset.autoHideInitialized = 'true'
        
        this.autoHideTimer = setTimeout(() => {
          this.hideMessage(message)
        }, this.hideDelayValue)
      }
    })
  }

  // 입장 애니메이션 추가
  addEntranceAnimation() {
    this.messageTargets.forEach(message => {
      if (!message.dataset.animationAdded) {
        message.dataset.animationAdded = 'true'
        
        // 초기 상태 설정
        message.style.opacity = '0'
        message.style.transform = 'translateY(-20px)'
        message.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out'
        
        // 애니메이션 실행
        setTimeout(() => {
          message.style.opacity = '1'
          message.style.transform = 'translateY(0)'
        }, 10)
      }
    })
  }

  // 메시지 수동 닫기
  dismiss(event) {
    const message = event.target.closest('[data-flash-message="true"]')
    if (message) {
      this.hideMessage(message)
    }
  }

  // 메시지 숨김 (애니메이션 포함)
  hideMessage(message) {
    if (!message || message.dataset.hiding) {
      return
    }
    
    message.dataset.hiding = 'true'
    
    // 페이드 아웃 애니메이션
    message.style.transition = 'opacity 0.5s ease-out, transform 0.5s ease-out, height 0.3s ease-out'
    message.style.opacity = '0'
    message.style.transform = 'translateY(-10px)'
    
    setTimeout(() => {
      message.style.height = '0'
      message.style.marginBottom = '0'
      message.style.padding = '0'
      
      setTimeout(() => {
        if (message.parentNode) {
          message.remove()
        }
      }, 300)
    }, 500)
  }

  // 모든 메시지 숨김
  hideAll() {
    this.messageTargets.forEach(message => {
      this.hideMessage(message)
    })
  }

  // 새 메시지 동적 추가 (Turbo Stream에서 사용)
  addMessage(type, text) {
    const container = this.element
    const messageHtml = this.createMessageHTML(type, text)
    
    // 기존 같은 메시지가 있는지 확인
    const existingMessage = Array.from(container.querySelectorAll('[data-flash-message="true"]'))
      .find(msg => {
        const existingText = msg.querySelector('span')?.textContent?.trim()
        const existingType = msg.dataset.flashType
        return existingText === text && existingType === type
      })
    
    if (!existingMessage) {
      container.insertAdjacentHTML('beforeend', messageHtml)
      
      // 새로 추가된 메시지에 애니메이션 적용
      const newMessage = container.lastElementChild
      this.addEntranceAnimation()
      
      if (this.autoHideValue) {
        setTimeout(() => {
          this.hideMessage(newMessage)
        }, this.hideDelayValue)
      }
    }
  }

  // 메시지 HTML 생성
  createMessageHTML(type, text) {
    const iconSVG = this.getIconSVG(type)
    const alertClass = type === 'notice' ? 'success' : type
    
    return `
      <div class="flash-message alert alert-${alertClass} mb-4 p-4 rounded-lg border" 
           data-turbo-temporary 
           data-flash-message="true"
           data-flash-type="${type}"
           data-flash-message-target="message">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            ${iconSVG}
            <span class="text-sm font-medium">${text}</span>
          </div>
          <button type="button" 
                  class="text-gray-400 hover:text-gray-600" 
                  data-action="click->flash-message#dismiss"
                  data-flash-message-target="closeButton">
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>
      </div>
    `
  }

  // 타입별 아이콘 SVG 반환
  getIconSVG(type) {
    switch (type) {
      case 'notice':
        return `<svg class="w-5 h-5 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>`
      case 'alert':
        return `<svg class="w-5 h-5 mr-2 text-red-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>`
      case 'warning':
        return `<svg class="w-5 h-5 mr-2 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>`
      default:
        return ''
    }
  }
}